// --------------- External imports -------------- //
import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { withLocalize } from 'react-localize-redux'
import { connect } from 'react-redux'
import _ from 'lodash'

// --------------- Material-UI imports -------------- //
import { Modal } from '@material-ui/core'

// --------------- Internal imports -------------- //
import { getUser } from 'common/selectors/user'
import { getSelectedProject } from 'common/selectors/projects'
import { getError, getIsSubmitting, getIsAddingEmail } from '../selectors'
import { updateAlarm } from 'common/actions/systems'
import { resetError } from 'common/actions/error'
import { hideModal } from 'common/actions/modal'
import EditParameterAlertModalForm from '../components/EditParameterAlertModalForm'
import { addAlertRecipient, deleteAlertRecipient } from 'common/actions/user'
import styles from './EditParameterAlertModalContainer.module.scss'
import ConfirmationModal from 'modules/modals/ConfirmationModal'
import DataTypes from 'constants/DataTypes'
import * as DataUtils from 'utils/data'
import { getCompleteMeasurementUnits } from 'utils/dataUnitsDefault'
import { getParameterAlarmConstraints } from 'common/selectors/alarmConstraints'

const getNormalizedValuesForType = (value, type, measurementUnits) => {
  if (type === 'tempLow' || type === 'tempHigh') type = DataTypes.TEMPERATURE
  const measurementUnit = measurementUnits[type]

  if (type === DataTypes.FORMALDEHYDE) {
    return Number(DataUtils.normalizeFormaldehyde(value, measurementUnit)).toFixed(0)
  }
  if (type === DataTypes.VOCS) {
    return Number(DataUtils.normalizeTvoc(value, measurementUnit)).toFixed(0)
  }
  if (type === DataTypes.VOCS_INDEX) {
    return Number(DataUtils.normalizeTvocIndex(value, measurementUnit)).toFixed(0)
  }
  if (type === DataTypes.O3) {
    return Number(DataUtils.normalizeO3(value, measurementUnit)).toFixed(0)
  }
  if (type === DataTypes.CO) {
    return Number(DataUtils.normalizeCO(value, measurementUnit)).toFixed(0)
  }
  if (type === DataTypes.NO2) {
    return Number(DataUtils.normalizeNo2(value, measurementUnit)).toFixed(0)
  }
  if (type === DataTypes.TEMPERATURE) {
    return Number(DataUtils.normalizeTemperature(value, measurementUnit)).toFixed(1)
  }
  return value
}

const hasPeriod = period => Array.isArray(period) && period.length === 2

export const getEditAlarmPayload = ({ data, user }) => {
  const measurementUnits = getCompleteMeasurementUnits(user.toJS().measurementUnits)
  const typesSelected = data.types.filter(Boolean)
  const Meta = typesSelected.reduce((values, type) => {
    values[type] = getNormalizedValuesForType(data.typesInput[type], type, measurementUnits)
    return values
  }, {})
  const emailRecipients = data.emailRecipients.map(email => email.toLowerCase())

  const alarmMailHours = {
    period1: data.period1,
    offset: data.offset,
    days: data.occupancyDays
  }

  if (hasPeriod(data.period2)) alarmMailHours.period2 = data.period2
  if (hasPeriod(data.auxPeriod)) {
    alarmMailHours.auxPeriod = data.auxPeriod
    alarmMailHours.auxDays = data.auxDays
  }
  if (hasPeriod(data.auxPeriod2)) alarmMailHours.auxPeriod2 = data.auxPeriod2

  return {
    Meta,
    name: data.alarmName,
    emailRecipients,
    active: true,
    alarmMailHours,
    selectedDevices: data.selectedDevices
  }
}

const EditParameterAlertModalContainer = props => {
  const [open] = useState(true)
  const [confirmingDeleteEmail, setConfirmingDeleteEmail] = useState(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const {
    hideModal,
    updateAlarm,
    resetError,
    systems,
    alarm,
    user,
    deleteAlertRecipient,
    translate,
    projectId
  } = props
  const systemsByGroup = _.groupBy(systems, 'group')

  const handleSubmit = data => {
    updateAlarm({
      AlarmId: alarm.AlarmId,
      jsonData: getEditAlarmPayload({ data, user })
    })
  }

  useEffect(() => {
    resetError()
  }, [resetError])

  const handleDeleteAlertRecipient = email => {
    setConfirmingDeleteEmail(email)
    setConfirmingDelete(true)
  }

  const confirmDeleteAlertRecipient = () => {
    deleteAlertRecipient({
      email: confirmingDeleteEmail,
      projectId,
      customerId: user.toJS()._id
    })
    setConfirmingDelete(false)
  }

  return (
    <div>
      <Modal
        open={open}
        onClose={hideModal}
        arial-labelledby='simple-modal-title'
        arial-desccribedby='simple-modal-description'
      >
        <div className={styles.content}>
          <EditParameterAlertModalForm
            {...props}
            systems={systems}
            systemsByGroup={systemsByGroup}
            hideModal={hideModal}
            onSubmit={handleSubmit}
            onDeleteAlertRecipient={handleDeleteAlertRecipient}
          />
          {confirmingDelete && (
            <ConfirmationModal
              hideModal={() => setConfirmingDelete(false)}
              translate={translate}
              onConfirm={confirmDeleteAlertRecipient}
              contentText={translate('deleteAlertRecipientConfirmationText')}
              contentDetail={confirmingDeleteEmail}
            />
          )}
        </div>
      </Modal>
    </div>
  )
}

EditParameterAlertModalContainer.propTypes = {
  hideModal: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  contentText: PropTypes.string,
  title: PropTypes.string,
  buttonName: PropTypes.string,
  parameterAlarmParamsBySystem: PropTypes.object
}

const mapStateToProps = state => ({
  user: getUser(state),
  submitError: getError(state),
  isSubmitting: getIsSubmitting(state),
  isAddingEmail: getIsAddingEmail(state),
  parameterAlarmParamsBySystem: getParameterAlarmConstraints(state),
  projects: getSelectedProject(state)
})

const mapActionsToProps = {
  updateAlarm,
  resetError,
  hideModal,
  deleteAlertRecipient,
  addAlertRecipient
}

export default withLocalize(
  connect(mapStateToProps, mapActionsToProps)(EditParameterAlertModalContainer)
)
